/* eslint-disable complexity */
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { Tab, TabList, TabPanel, Tabs } from 'carbon-react/lib/components/tabs/__next__';

import * as styles from './listing-page-tabs.scss';

interface ListingPageTabsProps {
  stickyTop?: number;
  /** Mount only the route-active tab content. Useful when hidden content cannot be measured reliably. */
  mountActiveTabOnly?: boolean;
  tabs: {
    id: string;
    title: string;
    component: ReactNode;
  }[];
  basePath?: string;
}

export const LISTING_PAGE_TABS_HEIGHT = 48;

export const ListingPageTabs = ({ tabs, stickyTop, basePath, mountActiveTabOnly = false }: ListingPageTabsProps) => {
  const top = stickyTop !== undefined ? stickyTop : undefined;
  const navigate = useNavigate();
  const intl = useIntl();
  const { pathname, search } = useLocation();
  const [selectedTabId, setSelectedTabId] = useState(tabs[0]?.id);

  const activeTabId = useMemo(() => {
    if (!basePath) {
      return undefined;
    }
    const suffix = pathname.replace(basePath, '').replace(/^\//, '');
    const match = tabs.find(tab => tab.id === suffix);
    return match?.id;
  }, [basePath, pathname, tabs]);

  useEffect(() => {
    if (!basePath) {
      return;
    }
    if (!activeTabId && tabs.length > 0) {
      navigate(`${basePath}/${tabs[0].id}${search}`, { replace: true });
    }
  }, [basePath, activeTabId, tabs, navigate, search]);

  useEffect(() => {
    if (basePath || !mountActiveTabOnly || tabs.some(tab => tab.id === selectedTabId)) {
      return;
    }

    setSelectedTabId(tabs[0]?.id);
  }, [basePath, mountActiveTabOnly, selectedTabId, tabs]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      if (basePath) {
        navigate(`${basePath}/${tabId}${search}`, { replace: true });
      } else if (mountActiveTabOnly) {
        setSelectedTabId(tabId);
      }
    },
    [basePath, mountActiveTabOnly, navigate, search]
  );

  const mountedTabId = basePath ? activeTabId ?? tabs[0]?.id : selectedTabId;

  return (
    <div className={styles.tabsContainer}>
      <div
        className={styles.carbonTabsContainer}
        style={{ '--sticky-top': top !== undefined ? `${top}px` : undefined } as React.CSSProperties}
      >
        <Tabs orientation="horizontal" {...(basePath || mountActiveTabOnly ? { selectedTabId: mountedTabId } : {})}>
          <TabList
            ariaLabel={intl.formatMessage({ id: 'common.tabs.aria_label', defaultMessage: 'Tabs' })}
            onTabChange={basePath || mountActiveTabOnly ? handleTabChange : undefined}
          >
            {tabs.map(tab => (
              <Tab key={tab.id} id={tab.id} controls={`${tab.id}-panel`} label={tab.title} />
            ))}
          </TabList>
          {tabs.map(tab => (
            <TabPanel key={tab.id} id={`${tab.id}-panel`} tabId={tab.id}>
              {!mountActiveTabOnly || tab.id === mountedTabId ? tab.component : null}
            </TabPanel>
          ))}
        </Tabs>
      </div>
    </div>
  );
};
